import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { useTranslatedUI } from '../hooks/useTranslatedUI';
import { ExternalLink, Mail } from 'lucide-react';

const FOOTER_STRINGS = {
  description: "KnowMySchemes (KMS) is India's AI-powered welfare intelligence platform. Our flagship tool, KMS Smart, matches your profile — profession, location, income — to the exact government schemes you qualify for, with estimated benefits in INR and step-by-step application guidance. Live job notifications (UPSC, SSC, Railway, Banking, State PSCs), real-time policy updates every 6 hours, and support for Hindi, Telugu, Kannada and English. 1000+ verified schemes across Central, AP, TG and KA — and growing.",
  quickLinks: "Quick Links",
  homeAbout: "Home / About",
  careers: "Careers",
  futureEnhancements: "Future Enhancements",
  contactUs: "Contact Us",
  address: "Address",
  allRights: "All rights reserved.",
};

// WhatsApp Icon Component
const WhatsAppIcon = ({ className }) => (
  <svg 
    viewBox="0 0 24 24" 
    fill="currentColor" 
    className={className}
  >
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
  </svg>
);

const Footer = () => {
  const { t } = useLanguage();
  const ui = useTranslatedUI(FOOTER_STRINGS);

  return (
    <footer className="bg-green-700 text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
          {/* Logo & Description */}
          <div className="space-y-3">
            {/* Logo */}
            <div className="flex items-center gap-3">
              <img 
                src="/images/kms-logo.png" 
                alt="Know My Schemes" 
                className="w-12 h-12 object-contain rounded-lg bg-white p-1"
              />
            </div>
            
            {/* Description */}
            <p className="text-green-100 text-xs leading-relaxed">
              {ui.description}
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-base font-semibold mb-3 text-white underline underline-offset-4 decoration-2">
              {ui.quickLinks}
            </h3>
            <ul className="space-y-2">
              <li>
                <Link 
                  to="/about" 
                  className="flex items-center gap-2 text-green-100 hover:text-white transition-colors group text-sm"
                >
                  <ExternalLink className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform" />
                  <span>{ui.homeAbout}</span>
                </Link>
              </li>
              <li>
                <Link 
                  to="/careers" 
                  className="flex items-center gap-2 text-green-100 hover:text-white transition-colors group text-sm"
                >
                  <ExternalLink className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform" />
                  <span>{ui.careers}</span>
                </Link>
              </li>
              <li>
                <Link 
                  to="/future" 
                  className="flex items-center gap-2 text-green-100 hover:text-white transition-colors group text-sm"
                >
                  <ExternalLink className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform" />
                  <span>{ui.futureEnhancements}</span>
                </Link>
              </li>
              <li>
                <Link 
                  to="/privacy" 
                  className="flex items-center gap-2 text-green-100 hover:text-white transition-colors group text-sm"
                >
                  <ExternalLink className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform" />
                  <span>Privacy Policy</span>
                </Link>
              </li>
              <li>
                <Link 
                  to="/terms" 
                  className="flex items-center gap-2 text-green-100 hover:text-white transition-colors group text-sm"
                >
                  <ExternalLink className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform" />
                  <span>Terms & Conditions</span>
                </Link>
              </li>
            </ul>
          </div>

          {/* Contact Us */}
          <div>
            <h3 className="text-base font-semibold mb-3 text-white underline underline-offset-4 decoration-2">
              {ui.contactUs}
            </h3>
            <ul className="space-y-3">
              <li className="flex items-start gap-3">
                <WhatsAppIcon className="w-4 h-4 text-green-300 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-green-200 text-xs">WhatsApp</p>
                  <a 
                    href="https://wa.me/919187636397" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-white font-medium hover:underline text-sm"
                  >
                    +91 91876 36397
                  </a>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <Mail className="w-4 h-4 text-green-300 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-green-200 text-xs">Email</p>
                  <a 
                    href="mailto:info@knowmyschemes.com" 
                    className="text-white font-medium hover:underline text-sm"
                  >
                    info@knowmyschemes.com
                  </a>
                </div>
              </li>
            </ul>
          </div>
        </div>

        {/* Divider */}
        <div className="border-t border-green-600 mt-6 pt-4">
          <p className="text-center text-green-200 text-xs">
            © {new Date().getFullYear()} Know My Schemes. {ui.allRights}
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
